import React, { useEffect, useState } from "react";
import axios from "axios";
import BASE_URL from "../config";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../components/Loader"; // Import Loader

const PAGE_SIZE_OPTIONS = [20, 50, 100, "All"];
const DEFAULT_PAGE_SIZE = 20;

export default function DatabaseShrinker() {
  const [dbs, setDbs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shrinking, setShrinking] = useState("");
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState(""); // Add search state
  // Set default sortBy to "size" and sortDir to "desc"
  const [sortBy, setSortBy] = useState("size");
  const [sortDir, setSortDir] = useState("desc");

  const fetchDbs = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${BASE_URL}api/get-database-sizes`);
      console.log("API /get-database-sizes response:", res.data);
      if (res.data && res.data.debug) {
        setError("Backend debug: " + res.data.debug + " | SQL: " + res.data.sql);
        setDbs([]);
      } else {
        setDbs(res.data);
      }
    } catch (e) {
      let detail = e.response?.data?.error || e.message;
      if (e.response?.data?.sqlsrv_errors) {
        detail += " | SQLSRV: " + JSON.stringify(e.response.data.sqlsrv_errors);
      }
      setError("Failed to load database sizes: " + detail);
      console.error("DB Shrinker error:", e);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchDbs();
  }, []);

  const handleShrink = async (dbName) => {
    setShrinking(dbName);
    setError("");
    try {
      const res = await axios.post(
        `${BASE_URL}api/get-database-sizes?db=${encodeURIComponent(dbName)}`
      );
      // Always expect JSON from Node.js backend
      const data = res.data;
      if (data?.error) {
        // Special case: not enough free space
        if (
          data.error === "Database files do not have enough free space to reclaim."
        ) {
          setError(
            "Shrink not needed: The database files are already as small as possible. No space can be reclaimed."
          );
          toast.dismiss();
          toast.error("Shrink not needed: The database files are already as small as possible. No space can be reclaimed.", { autoClose: 10000 });
          setShrinking("");
          return;
        }
        const errMsg = "Shrink failed: " + data.error +
          (data.info ? " | Info: " + data.info : "") +
          (data.sqlsrv_errors ? " | SQLSRV: " + JSON.stringify(data.sqlsrv_errors) : "") +
          (data.sql ? " | SQL: " + data.sql : "") +
          (data.dbName ? " | dbName: " + data.dbName : "");
        setError(errMsg);
        toast.dismiss();
        toast.error(errMsg, { autoClose: 10000 });
        setShrinking("");
        return;
      }
      // Show success in toaster only
      toast.dismiss();
      toast.success(data?.message || "Shrink executed", { autoClose: 10000 });
      await fetchDbs();
    } catch (e) {
      const errMsg = "Shrink failed: " + (e.message || "Unknown error");
      setError(errMsg);
      toast.dismiss();
      toast.error(errMsg, { autoClose: 10000 });
      setShrinking("");
    }
    setShrinking("");
  };

  // Filtered and paginated data
  const filteredDbs = dbs.filter(db => {
    const name = db.DatabaseName ?? db.DATABASENAME ?? db.name ?? db.NAME ?? "";
    return name.toLowerCase().includes(search.toLowerCase());
  });
  const totalPages =
    pageSize === "All" ? 1 : Math.ceil(filteredDbs.length / pageSize);

  // Sorting logic
  const getSortValue = (db, key) => {
    if (key === "id") {
      return db.database_id ?? db.DATABASE_ID ?? db.id ?? db.ID ?? 0;
    }
    if (key === "name") {
      return (db.DatabaseName ?? db.DATABASENAME ?? db.name ?? db.NAME ?? "").toLowerCase();
    }
    if (key === "size") {
      // Extract number from "123 MB"
      const sizeStr = db.Size ?? db.SIZE ?? db.size ?? "";
      const num = parseFloat((sizeStr + "").replace(/[^\d.]/g, "")) || 0;
      return num;
    }
    return "";
  };

  const sortedDbs = [...filteredDbs].sort((a, b) => {
    const valA = getSortValue(a, sortBy);
    const valB = getSortValue(b, sortBy);
    if (valA < valB) return sortDir === "asc" ? -1 : 1;
    if (valA > valB) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const pagedData =
    pageSize === "All"
      ? sortedDbs
      : sortedDbs.slice((page - 1) * pageSize, page * pageSize);

  // Helper for sort icon
  const sortIcon = (col) =>
    sortBy === col ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  // Pagination logic (show up to 5 page numbers centered around current page)
  const getPageNumbers = () => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + 4);
    if (end - start < 4) start = Math.max(1, end - 4);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  // UI Styles (for attractiveness and consistency)
  const containerStyle = {
    padding: 32,
    maxWidth: 900,
    margin: "32px auto",
    background: "#fff",
    borderRadius: 12,
    boxShadow: "0 2px 16px rgba(0,0,0,0.08)",
    fontFamily: "Segoe UI, Arial, sans-serif"
  };
  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: 24,
    background: "#fafbfc"
  };
  const thStyle = {
    background: "#1976d2",
    color: "#fff",
    fontWeight: 600,
    padding: "12px 8px",
    border: "1px solid #e0e0e0"
  };
  const tdStyle = {
    padding: "10px 8px",
    border: "1px solid #e0e0e0",
    textAlign: "center"
  };
  const shrinkBtnStyle = (enabled) => ({
    background: enabled ? "#1976d2" : "#bdbdbd",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    padding: "6px 16px",
    cursor: enabled ? "pointer" : "not-allowed",
    fontWeight: 500
  });
  const paginationStyle = {
    margin: "24px 0 0 0",
    display: "flex",
    justifyContent: "center",
    gap: 8
  };
  const pageBtnStyle = (active) => ({
    background: active ? "#1976d2" : "#e3e3e3",
    color: active ? "#fff" : "#333",
    border: "none",
    borderRadius: 4,
    padding: "6px 12px",
    cursor: "pointer",
    fontWeight: 500
  });

  return (
    <div style={containerStyle}>
      <ToastContainer />
      {/* Show Loader overlay during shrinking or loading */}
      {(shrinking || loading) && (
        <Loader message={shrinking ? "Shrinking database..." : "Loading..."} />
      )}
      <h2 style={{ color: "#1976d2", marginBottom: 0 }}>Database Shrinker</h2>
      <div style={{ color: "#666", marginBottom: 16 }}>
        View and manage your SQL Server databases. Shrink is only enabled for allowed databases.
      </div>
      {/* Inline search and page size selector */}
      <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 16 }}>
        <span style={{ fontWeight: 500 }}>Search:</span>
        <input
          type="text"
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder="Database name..."
          style={{
            padding: "4px 8px",
            borderRadius: 4,
            border: "1px solid #bdbdbd",
            fontSize: 15,
            minWidth: 180
          }}
        />
        <span style={{ fontWeight: 500, marginLeft: 24 }}>Rows per page:</span>
        <select
          value={pageSize}
          onChange={e => {
            const val = e.target.value === "All" ? "All" : parseInt(e.target.value, 10);
            setPageSize(val);
            setPage(1);
          }}
          style={{
            padding: "4px 8px",
            borderRadius: 4,
            border: "1px solid #bdbdbd",
            fontSize: 15
          }}
        >
          {PAGE_SIZE_OPTIONS.map(opt => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>
      {loading ? (
        <div style={{ fontSize: 18, color: "#1976d2" }}>Loading...</div>
      ) : (
        <>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th
                  style={thStyle}
                  onClick={() => {
                    setSortBy("id");
                    setSortDir(sortBy === "id" && sortDir === "asc" ? "desc" : "asc");
                  }}
                  title="Sort by ID"
                >
                  ID{sortIcon("id")}
                </th>
                <th
                  style={thStyle}
                  onClick={() => {
                    setSortBy("name");
                    setSortDir(sortBy === "name" && sortDir === "asc" ? "desc" : "asc");
                  }}
                  title="Sort by Database Name"
                >
                  Database Name{sortIcon("name")}
                </th>
                <th
                  style={thStyle}
                  onClick={() => {
                    setSortBy("size");
                    setSortDir(sortBy === "size" && sortDir === "asc" ? "desc" : "asc");
                  }}
                  title="Sort by Size"
                >
                  Size{sortIcon("size")}
                </th>
                <th style={thStyle}>Shrink</th>
              </tr>
            </thead>
            <tbody>
              {pagedData.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ ...tdStyle, textAlign: "center" }}>
                    No data found.
                  </td>
                </tr>
              ) : (
                pagedData.map((db, idx) => {
                  const id = db.database_id ?? db.DATABASE_ID ?? db.id ?? db.ID;
                  const name = db.DatabaseName ?? db.DATABASENAME ?? db.name ?? db.NAME;
                  const size = db.Size ?? db.SIZE ?? db.size;
                  const shrinkEnabled = (shrinking !== name);
                  return (
                    <tr key={id || idx}>
                      <td style={tdStyle}>{id}</td>
                      <td style={tdStyle}>{name}</td>
                      <td style={tdStyle}>{size}</td>
                      <td style={tdStyle}>
                        <button
                          style={shrinkBtnStyle(shrinkEnabled)}
                          disabled={!shrinkEnabled}
                          onClick={() => handleShrink(name)}
                        >
                          {shrinking === name ? "Shrinking..." : "Shrink"}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          {/* Pagination Controls - page by page, below table */}
          {totalPages > 1 && (
            <div style={{ ...paginationStyle, marginTop: 16 }}>
              <button
                style={pageBtnStyle(false)}
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Prev
              </button>
              {getPageNumbers().map(num => (
                <button
                  key={num}
                  style={pageBtnStyle(page === num)}
                  onClick={() => setPage(num)}
                >
                  {num}
                </button>
              ))}
              <button
                style={pageBtnStyle(false)}
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
